import { z } from "zod";
import { MANIFEST_KINDS, eventTextProblem, normalizeEventText, type ManifestKind } from "@cx/domain";
import { listQuery, type ListResult } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import type { FiscalProviderId } from "./fiscal";

// ===== Notas de entrada (T2.5, RF-SUP-04): notas recebidas (DistDFe/upload), manifestação e lançamento =====

/** "Notas recebidas" (todas) e "Notas de compra" (com a entrada lançada). */
export const INBOUND_DFE_TABS = ["received", "purchases"] as const;
export type InboundDfeTab = (typeof INBOUND_DFE_TABS)[number];
export const inboundDfeListQuery = listQuery.extend({
  tab: z.enum(INBOUND_DFE_TABS).default("received"),
});
export type InboundDfeListQuery = z.output<typeof inboundDfeListQuery>;

export type InboundXmlStatus = "summary" | "full";
export type InboundImportStatus = "none" | "imported" | "reversed";
export type InboundSefazStatus = "authorized" | "denied" | "canceled";
export type InboundMatchSource = "supplier_code" | "gtin" | "ncm_description" | "manual" | "ai";
/** Teto de itens de uma nota (o leiaute aceita 990). */
export const INBOUND_MAX_ITEMS = 990;
/** Itens por pedido ao Conciliador de XML (IA). */
export const DFE_AI_MAX_ITEMS = 50;
/** Maior XML aceito no envio manual. */
export const INBOUND_XML_MAX_BYTES = 1_000_000;

export type InboundDfeListItem = {
  id: string;
  accessKey: string;
  number: number | null;
  series: number | null;
  issuerName: string | null;
  issuerDocument: string | null;
  issuedAt: string | null;
  totalCents: number;
  source: "distdfe" | "upload";
  environment: 1 | 2;
  xmlStatus: InboundXmlStatus;
  sefazStatus: InboundSefazStatus;
  manifestStatus: ManifestKind | null;
  /** Manifestação em envio. */
  manifestPending: ManifestKind | null;
  importStatus: InboundImportStatus;
  importedAt: string | null;
  createdAt: string;
};
export type InboundDfeList = ListResult<InboundDfeListItem> & {
  summary: { count: number; totalCents: number };
};

export type InboundTaxValue = { baseCents?: number; pct4?: number; valueCents: number };
/** Impostos do item no XML (créditos possíveis), em centavos e pct4. */
export type InboundItemTaxes = Partial<
  Record<"icms" | "icmsSt" | "fcpSt" | "ipi" | "pis" | "cofins", InboundTaxValue>
>;

export type DfeMatchProposal = z.output<typeof dfeMatchProposal>;
export type AiSuggestionStatus = "pending" | "accepted" | "rejected" | "expired";
export type AiSuggestionView = {
  id: string;
  agent: string;
  entityType: string | null;
  entityId: string | null;
  parentId: string | null;
  proposal: DfeMatchProposal;
  rationale: string | null;
  /** 0..1.000.000 (pct4). */
  confidencePct4: number | null;
  status: AiSuggestionStatus;
  createdAt: string;
  decidedAt: string | null;
};

export type InboundDfeItemView = {
  id: string;
  position: number;
  code: string;
  description: string;
  ncm: string | null;
  cest: string | null;
  cfop: string | null;
  gtin: string | null;
  gtinTax: string | null;
  unit: string;
  quantityQ4: number;
  unitValue: string;
  taxUnit: string | null;
  taxQuantityQ4: number | null;
  totalCents: number;
  costCents: number;
  taxes: InboundItemTaxes;
  productId: string | null;
  productName: string | null;
  productSku: string | null;
  productUnit: string | null;
  matchSource: InboundMatchSource | null;
  factorQ4: number;
  /** Quantidade que entra no estoque (unidade do produto). */
  entryQtyQ4: number;
  unitCostCents: number;
  needsReview: boolean;
  createProduct: boolean;
  newProductName: string | null;
  suggestion: AiSuggestionView | null;
};

export type InboundManifestEventView = {
  id: string;
  kind: ManifestKind;
  status: "pending" | "sending" | "registered" | "rejected";
  text: string | null;
  protocol: string | null;
  statusCode: string | null;
  statusMessage: string | null;
  canRefresh: boolean;
  registeredAt: string | null;
  createdAt: string;
};

export type InboundDfeView = InboundDfeListItem & {
  protocol: string | null;
  operationType: number | null;
  issuerIe: string | null;
  supplier: { id: string; name: string } | null;
  totals: Record<string, number> | null;
  installments: { number: string | null; dueDate: string; amountCents: number }[];
  items: InboundDfeItemView[];
  events: InboundManifestEventView[];
  entry: {
    warehouseId: string | null;
    movements: number;
    titles: number;
    importedAt: string | null;
    reversedAt: string | null;
  } | null;
  /**
   * A SEFAZ confirmou a nota (veio pela distribuição ou o XML enviado completou um resumo dela). Falso = XML enviado
   * pelo usuário, assinatura não conferida: a tela avisa antes de lançar as contas a pagar.
   */
  sefazConfirmed: boolean;
  /** Pedido de compra que esta entrada recebeu (T2.6); null = nenhum ou recebimento estornado. */
  purchaseOrder: { id: string; number: number; receiptNumber: number } | null;
  /** Motivos que impedem o lançamento agora (vazio = pode lançar). */
  importBlockers: string[];
  /** Manifestações que o usuário pode pedir agora. */
  allowedManifests: ManifestKind[];
  unmatchedCount: number;
};

export type DfeSyncStatus = {
  provider: FiscalProviderId;
  cnpj: string | null;
  autoSync: boolean;
  autoCiencia: boolean;
  status: "idle" | "queued" | "running";
  ultNsu: number;
  maxNsu: number;
  nextAllowedAt: string | null;
  lastCode: string | null;
  lastMessage: string | null;
  lastRunAt: string | null;
};

export const dfeSettingsInput = z.object({ autoSync: z.boolean(), autoCiencia: z.boolean() });
export type DfeSettingsInput = z.output<typeof dfeSettingsInput>;

export const manifestInput = z
  .object({
    kind: z.enum(MANIFEST_KINDS, "Manifestação inválida"),
    justification: z.string().max(4000, "Texto longo demais").nullish(),
  })
  .superRefine((v, ctx) => {
    if (v.kind !== "nao_realizada") return;
    const p = eventTextProblem(v.justification ?? "", "reason");
    if (p) ctx.addIssue({ code: "custom", path: ["justification"], message: p });
  })
  // Só a "operação não realizada" leva justificativa (Latin-1, sem quebras: a mesma regra dos eventos da T2.4).
  .transform((v) => ({
    kind: v.kind,
    justification: v.kind === "nao_realizada" ? normalizeEventText(v.justification ?? "") : null,
  }));
export type ManifestInput = z.output<typeof manifestInput>;

const factorQ4 = z
  .number("Informe o fator")
  .int("Fator inválido")
  .min(1, "Fator deve ser maior que zero")
  .max(1_000_000_000, "Fator acima do limite");

/** Vínculo manual de um item: produto (ou nenhum), fator de conversão, ou "cadastrar produto novo". */
export const inboundItemInput = z
  .object({
    productId: ulidParam.nullable(),
    factorQ4,
    createProduct: z.boolean().default(false),
    newProductName: text(120),
  })
  .superRefine((v, ctx) => {
    if (v.createProduct && v.productId)
      ctx.addIssue({
        code: "custom",
        path: ["productId"],
        message: "Escolha um produto ou cadastre um novo, não os dois",
      });
  });
export type InboundItemInput = z.output<typeof inboundItemInput>;

export const importInboundInput = z.object({
  warehouseId: ulidParam.nullish().transform((v) => v ?? null),
  postStock: z.boolean().default(true),
  postPayables: z.boolean().default(true),
  entryDate: businessDate.optional(),
  /**
   * Pedido de compra recebido por esta nota (T2.6): a entrada vira o recebimento do pedido (quantidades casadas por
   * produto, sem estoque nem contas em dobro). Exige purchase_orders:update.
   */
  purchaseOrderId: ulidParam.nullish().transform((v) => v ?? null),
});
export type ImportInboundInput = z.output<typeof importInboundInput>;

/** O que o Conciliador de XML propõe para um item (validado de novo no aceite). */
export const dfeMatchProposal = z.object({
  action: z.enum(["match", "new"]),
  productId: ulidParam.nullable(),
  productName: z.string().max(120).nullable(),
  newProductName: z.string().max(120).nullable(),
  factorQ4,
});

export const aiSuggestionListQuery = z.object({
  parentId: ulidParam,
  status: z.enum(["pending", "accepted", "rejected", "expired"]).optional(),
});
export type AiSuggestionListQuery = z.output<typeof aiSuggestionListQuery>;

// ---------- dados que o Worker extrai do XML e entrega ao TenantDO ----------

export type InboundAddress = {
  street: string | null;
  number: string | null;
  complement: string | null;
  district: string | null;
  cityIbge: string | null;
  city: string | null;
  uf: string | null;
  cep: string | null;
};

export type ParsedInboundItem = {
  position: number;
  code: string;
  description: string;
  ncm: string | null;
  cest: string | null;
  cfop: string | null;
  gtin: string | null;
  gtinTax: string | null;
  origin: number | null;
  unit: string;
  quantityQ4: number;
  /** vUnCom como veio (até 10 casas). */
  unitValue: string;
  taxUnit: string | null;
  taxQuantityQ4: number | null;
  totalCents: number;
  discountCents: number;
  freightCents: number;
  insuranceCents: number;
  otherCents: number;
  ipiCents: number;
  stCents: number;
  fcpStCents: number;
  taxes: InboundItemTaxes;
  /** Grupo `rastro` (T3.2): lotes do item em unidades do fornecedor (qLote em q4). */
  lots?: { code: string; qtyQ4: number; manufacturedAt: string | null; expiresAt: string | null }[];
};

/** NF-e (procNFe ou NFe) do fornecedor já validada pelo parser estrito. */
export type ParsedInboundNfe = {
  accessKey: string;
  environment: 1 | 2;
  number: number;
  series: number;
  issuedAt: string;
  operationType: number;
  /** finNFe: 1 normal, 2 complementar, 3 ajuste, 4 devolução (ausente = 1). */
  purpose?: number;
  issuer: {
    document: string;
    name: string;
    tradeName: string | null;
    ie: string | null;
    crt: number | null;
    address: InboundAddress;
  };
  recipientDocument: string;
  protocol: { number: string; status: string; receivedAt: string | null } | null;
  totals: {
    productsCents: number;
    discountCents: number;
    freightCents: number;
    insuranceCents: number;
    otherCents: number;
    ipiCents: number;
    stCents: number;
    fcpStCents: number;
    icmsCents: number;
    pisCents: number;
    cofinsCents: number;
    totalCents: number;
  };
  items: ParsedInboundItem[];
  installments: { number: string | null; dueDate: string; amountCents: number }[];
};

/** Resumo da NF-e (resNFe) da distribuição. */
export type ParsedDfeSummary = {
  accessKey: string;
  issuerDocument: string;
  issuerName: string;
  issuerIe: string | null;
  issuedAt: string | null;
  operationType: number | null;
  totalCents: number;
  sefazStatus: InboundSefazStatus;
  protocol: string | null;
};

/** Documento de um lote da distribuição, com o XML já gravado no R2 (`r2Key`). */
export type DfeIncoming =
  | { nsu: number | null; kind: "summary"; summary: ParsedDfeSummary; r2Key: string }
  | { nsu: number | null; kind: "full"; nfe: ParsedInboundNfe; r2Key: string }
  | { nsu: number | null; kind: "event"; accessKey: string; eventCode: string; r2Key: string | null };
